import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { LMS_SUPPORTED_LANGUAGES } from '../../lookup-definitions/constants/lmsLanguages';

const PRECONDITION_MAX_LENGTH = 500;
const DESCRIPTION_MAX_LENGTH = 1000;

const getFlagClassName = (languageCode) => {
    const codeToFlagSuffixMap = { en: 'us', tr: 'tr', de: 'de', fr: 'fr', it: 'it', ro: 'ro' };
    const suffix = codeToFlagSuffixMap[languageCode];
    return suffix ? `fi fi-${suffix}` : 'fi fi-xx';
};

const blankTranslations = () => LMS_SUPPORTED_LANGUAGES.map((lang) => ({ languageId: lang.id, precondition: '', description: '' }));

/**
 * Ön Koşul Tanımları ekleme/düzenleme formu. Bkz. docs/LMS-Egitim-Katalogu-ve-Egitmenler-API 2.md §6.
 *
 * Her dilde `precondition` zorunlu, `description` opsiyoneldir. `code` backend tarafından
 * üretilir; bu ekranda hiç gösterilmez (doc §6.6: "Kod alanı ekranda gösterilmez veya
 * salt okunur gösterilir" — burada tercih ilkidir, tabloda salt okunur olarak gösterilir).
 */
const PreconditionDefinitionForm = ({ visible, onHide, onSubmit, initialData, loading, handleDelete, readOnly = false }) => {
    const { th } = useLanguage();
    const toast = useRef(null);

    const [formData, setFormData] = useState({
        id: null,
        translations: blankTranslations()
    });
    const [localSubmitted, setLocalSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    const isEdit = !!(initialData && initialData.id);

    useEffect(() => {
        if (!visible) return;

        const backendTranslations = (initialData && initialData.translations) || [];
        const mergedTranslations = LMS_SUPPORTED_LANGUAGES.map((lang) => {
            const existing = backendTranslations.find((t) => t.languageId === lang.id);
            return existing ? { languageId: lang.id, precondition: existing.precondition || '', description: existing.description || '' } : { languageId: lang.id, precondition: '', description: '' };
        });

        setFormData({
            id: (initialData && initialData.id) ?? null,
            translations: mergedTranslations
        });
        setLocalSubmitted(false);
        setErrors({});
    }, [visible, initialData]);

    const handleFieldChange = (languageId, field, value) => {
        setFormData((prev) => ({
            ...prev,
            translations: prev.translations.map((t) => (t.languageId === languageId ? { ...t, [field]: value } : t))
        }));

        if (field === 'precondition') {
            const lang = LMS_SUPPORTED_LANGUAGES.find((l) => l.id === languageId);
            if (lang && errors['precondition_' + lang.code] && value.trim()) {
                setErrors((prev) => {
                    const next = { ...prev };
                    delete next['precondition_' + lang.code];
                    return next;
                });
            }
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLocalSubmitted(true);

        const newErrors = {};
        const validationMessages = [];

        // Tüm 6 dilde precondition zorunlu — bkz. doc §6.2
        LMS_SUPPORTED_LANGUAGES.forEach((lang) => {
            const translation = formData.translations.find((t) => t.languageId === lang.id);
            if (!translation || !translation.precondition.trim()) {
                const msg = th('preconditionDefinitions.preconditionRequired', { language: lang.name });
                newErrors['precondition_' + lang.code] = msg;
                validationMessages.push(msg);
            }
        });

        setErrors(newErrors);

        if (validationMessages.length > 0) {
            validationMessages.forEach((detail, index) => {
                setTimeout(() => {
                    toast.current?.show({ severity: 'error', summary: th('preconditionDefinitions.missingField'), detail, life: 4000 });
                }, index * 150);
            });
            return;
        }

        const payload = {
            translations: formData.translations.map((t) => ({ languageId: t.languageId, precondition: t.precondition.trim(), description: (t.description || '').trim() }))
        };

        if (isEdit) {
            payload.id = formData.id;
        }

        onSubmit(payload);
    };

    const renderFooter = () => (
        <div>
            {isEdit && handleDelete && <Button label={th('common.delete')} icon="pi pi-trash" severity="danger" type="button" onClick={() => handleDelete(formData.id)} disabled={loading} />}
            <Button label={th('common.cancel')} icon="pi pi-times" onClick={onHide} outlined type="button" />
            {!readOnly && <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} loading={!!loading} />}
        </div>
    );

    return (
        <Dialog visible={visible} footer={renderFooter()} style={{ width: '45vw' }} header={isEdit ? th('preconditionDefinitions.editTitle') : th('preconditionDefinitions.addTitle')} modal className="p-fluid" onHide={onHide} key={formData.id ?? 'new'}>
            <Toast ref={toast} />
            <form onSubmit={handleSubmit} className="p-fluid">
                <div className="flex flex-column gap-4">
                    {LMS_SUPPORTED_LANGUAGES.map((lang) => {
                        const translation = formData.translations.find((t) => t.languageId === lang.id);
                        const currentPrecondition = (translation && translation.precondition) || '';
                        const currentDescription = (translation && translation.description) || '';
                        const preconditionId = `precondition-${lang.code}`;
                        const descriptionId = `description-${lang.code}`;
                        const hasError = !!errors['precondition_' + lang.code];
                        const showError = localSubmitted && (hasError || !currentPrecondition.trim());

                        return (
                            <div key={lang.id} className="p-3 border-1 border-round" style={{ borderColor: '#E5E7EB' }}>
                                <div className="font-semibold mb-2">
                                    <span className={getFlagClassName(lang.code)} style={{ fontSize: '1.2em', marginRight: '0.5em' }} title={lang.name} />
                                    {lang.name}
                                </div>
                                <div className="grid">
                                    <div className="col-12">
                                        <div className="field">
                                            <label htmlFor={preconditionId} className="flex justify-content-between align-items-center">
                                                <span>
                                                    {th('preconditionDefinitions.preconditionLabel')} <span className="text-red-500">*</span>
                                                </span>
                                                <small className={currentPrecondition.length >= PRECONDITION_MAX_LENGTH ? 'text-red-500' : 'text-color-secondary'}>
                                                    {currentPrecondition.length}/{PRECONDITION_MAX_LENGTH}
                                                </small>
                                            </label>
                                            <InputText id={preconditionId} value={currentPrecondition} maxLength={PRECONDITION_MAX_LENGTH} onChange={(e) => handleFieldChange(lang.id, 'precondition', e.target.value)} className={showError ? 'p-invalid' : ''} />
                                            {showError && <small className="p-error">{errors['precondition_' + lang.code] || th('preconditionDefinitions.preconditionRequired', { language: lang.name })}</small>}
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="field mb-0">
                                            <label htmlFor={descriptionId} className="flex justify-content-between align-items-center">
                                                <span>{th('preconditionDefinitions.descriptionLabel')}</span>
                                                <small className={currentDescription.length >= DESCRIPTION_MAX_LENGTH ? 'text-red-500' : 'text-color-secondary'}>
                                                    {currentDescription.length}/{DESCRIPTION_MAX_LENGTH}
                                                </small>
                                            </label>
                                            <InputTextarea id={descriptionId} rows={2} autoResize value={currentDescription} maxLength={DESCRIPTION_MAX_LENGTH} onChange={(e) => handleFieldChange(lang.id, 'description', e.target.value)} className="w-full" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </form>
        </Dialog>
    );
};

export default PreconditionDefinitionForm;
